import React from "react";

export default function HigherStudies({ course }) {
  const higherStudies = course?.higherStudies;

  return (
    <section
      id="higher-studies"
      className="relative overflow-hidden bg-[#EEF4F8] py-20 sm:py-24 lg:py-28"
    >
      {/* =====================================================
          BACKGROUND DETAILS
      ====================================================== */}

      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute
          right-[-160px]
          top-[-160px]
          h-[430px]
          w-[430px]
          rounded-full
          border
          border-[#071A2F]/[0.04]
        "
      />

      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute
          right-[-70px]
          top-[-70px]
          h-[250px]
          w-[250px]
          rounded-full
          border
          border-[#8B1E3F]/[0.05]
        "
      />

      <div className="relative mx-auto w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">

        {/* =====================================================
            HEADER
        ====================================================== */}

        <header className="grid gap-10 lg:grid-cols-[1.15fr_.65fr] lg:items-end lg:gap-20">

          <div>
            <div className="flex items-center gap-3 text-sm font-bold uppercase tracking-[0.16em] text-[#8B1E3F]">
              <span className="h-px w-10 bg-current" />
              Academic Progression
            </div>

            <h2 className="mt-6 max-w-[900px] font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(42px,5vw,70px)] font-normal leading-[1.03] tracking-[-0.04em] text-[#071A2F]">
              Higher Studies After{" "}
              <em className="font-normal text-[#8B1E3F]">
                {course?.shortTitle ||
                  course?.title ||
                  "This Programme"}
              </em>
            </h2>
          </div>

          <p className="max-w-[470px] text-base leading-8 text-[#667481]">
            Your undergraduate qualification can become the foundation for
            deeper specialisation, advanced academic study and new
            professional directions.
          </p>
        </header>

        {/* =====================================================
            ACADEMIC PATHWAY MAP
        ====================================================== */}

        <div className="mt-16 lg:mt-20">

          <div className="grid gap-8 lg:grid-cols-[360px_1fr] lg:gap-0">

            {/* =================================================
                STARTING POINT
            ================================================== */}

            <div className="relative z-[2]">

              <div className="relative h-full overflow-hidden bg-[#071A2F] px-8 py-10 text-white sm:px-10 lg:min-h-[560px] lg:px-11 lg:py-12">

                {/* decorative degree symbol */}

                <span
                  aria-hidden="true"
                  className="
                    pointer-events-none
                    absolute
                    -bottom-12
                    -right-5
                    font-['Libre_Caslon_Display',Georgia,serif]
                    text-[190px]
                    leading-none
                    text-white/[0.035]
                  "
                >
                  +
                </span>

                <div className="relative flex h-full flex-col justify-between">

                  <div>
                    <span className="text-xs font-bold uppercase tracking-[0.17em] text-[#D5AD57]">
                      Your Starting Point
                    </span>

                    <h3 className="mt-6 font-['Libre_Caslon_Display',Georgia,serif] text-[34px] font-normal leading-[1.15] sm:text-[38px]">
                      {course?.shortTitle ||
                        course?.title ||
                        "Your Degree"}
                    </h3>

                    <p className="mt-6 text-[15px] leading-7 text-white/60">
                      Completing this programme can open pathways towards
                      postgraduate education, specialised learning and
                      research-oriented opportunities.
                    </p>
                  </div>

                  <div className="mt-14">

                    <div className="mb-6 h-px w-full bg-white/15" />

                    <div className="flex items-center gap-5">
                      <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full border border-[#D5AD57]/40 text-xl text-[#D5AD57]">
                        ↗
                      </span>

                      <div>
                        <span className="block text-xs font-bold uppercase tracking-[0.14em] text-white/45">
                          Next
                        </span>

                        <strong className="mt-1 block font-['Libre_Caslon_Display',Georgia,serif] text-[21px] font-normal">
                          Choose your academic direction
                        </strong>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>

            {/* =================================================
                PATHWAYS
            ================================================== */}

            <div className="relative lg:pl-20">

              {/* MAIN CONNECTOR */}

              <span
                aria-hidden="true"
                className="
                  absolute
                  bottom-[55px]
                  left-[39px]
                  top-[55px]
                  hidden
                  w-px
                  bg-[#071A2F]/15
                  lg:block
                "
              />

              {/* CONNECTION FROM START */}

              <span
                aria-hidden="true"
                className="
                  absolute
                  left-0
                  top-1/2
                  hidden
                  h-px
                  w-10
                  -translate-y-1/2
                  bg-[#071A2F]/20
                  lg:block
                "
              />

              <div>
                {higherStudies.map((item, index) => (
                  <article
                    key={item.degree}
                    className="
                      group
                      relative
                      border-b
                      border-[#071A2F]/10
                      py-7
                      first:border-t
                      lg:min-h-[112px]
                      lg:py-6
                    "
                  >

                    {/* CONNECTOR NODE */}

                    <span
                      aria-hidden="true"
                      className="
                        absolute
                        left-[-47px]
                        top-1/2
                        hidden
                        h-[15px]
                        w-[15px]
                        -translate-y-1/2
                        rounded-full
                        border-[4px]
                        border-[#EEF4F8]
                        bg-[#8B1E3F]
                        ring-1
                        ring-[#8B1E3F]/25
                        lg:block
                      "
                    />

                    {/* HORIZONTAL CONNECTOR */}

                    <span
                      aria-hidden="true"
                      className="
                        absolute
                        left-[-40px]
                        top-1/2
                        hidden
                        h-px
                        w-10
                        bg-[#071A2F]/15
                        lg:block
                      "
                    />

                    <div className="grid gap-4 sm:grid-cols-[190px_1fr_40px] sm:items-center sm:gap-8">

                      {/* DEGREE */}

                      <div>
                        <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#8B1E3F]">
                          {item.field}
                        </span>

                        <h3 className="mt-2 font-['Libre_Caslon_Display',Georgia,serif] text-[24px] font-normal leading-[1.2] text-[#071A2F]">
                          {item.degree}
                        </h3>
                      </div>

                      {/* DESCRIPTION */}

                      <p className="max-w-[530px] text-sm leading-7 text-[#667481]">
                        {item.description}
                      </p>

                      {/* ARROW */}

                      <span
                        className="
                          grid
                          h-10
                          w-10
                          place-items-center
                          rounded-full
                          border
                          border-[#071A2F]/10
                          text-[#8B1E3F]
                          transition-all
                          duration-300
                          group-hover:border-[#8B1E3F]
                          group-hover:bg-[#8B1E3F]
                          group-hover:text-white
                        "
                      >
                        ↗
                      </span>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* =====================================================
            ACADEMIC DIRECTION STATEMENT
        ====================================================== */}

        <div className="mt-14 border-y border-[#071A2F]/10">

          <div className="grid lg:grid-cols-[220px_1fr_auto] lg:items-center">

            <div className="border-[#071A2F]/10 py-8 lg:border-r lg:pr-8">
              <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#8B1E3F]">
                Think Ahead
              </span>
            </div>

            <div className="py-8 lg:px-10">
              <p className="max-w-[780px] font-['Libre_Caslon_Display',Georgia,serif] text-[25px] font-normal leading-[1.45] text-[#071A2F] sm:text-[29px]">
                Your next qualification can help you{" "}
                <span className="text-[#8B1E3F]">
                  specialise deeper, broaden your expertise
                </span>{" "}
                or move towards research and academia.
              </p>
            </div>

            <div className="hidden py-8 lg:block">
              <span className="font-['Libre_Caslon_Display',Georgia,serif] text-[42px] text-[#8B1E3F]">
                →
              </span>
            </div>

          </div>
        </div>

        {/* =====================================================
            NOTE
        ====================================================== */}

        <div className="mt-7 flex max-w-[900px] items-start gap-4">

          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#8B1E3F]" />

          <p className="text-sm leading-7 text-[#74808B]">
            Higher-study options are indicative and may vary according to the
            student&apos;s previous qualifications, chosen specialisation,
            entrance requirements and eligibility criteria prescribed by the
            respective institution or programme.
          </p>

        </div>
      </div>
    </section>
  );
}